import { isClassificationColoringOn, resolveClassificationColor } from '@/lib/classification-color';
import { resolveTableCellColor } from '@/lib/table-cell-color';
import {
    getContrastTextColor,
    getHeaderLabels,
    getHeaderRows,
    normalizeTableBlock,
    resolveTableHeaderColor,
} from '@/lib/table-utils';
import { ReportFootnoteMarker } from '../ReportFootnotes';
import { findMetricId } from '../test-table/utils';
import StandaloneRichText from './StandaloneRichText';
import type { ReportFootnote } from '../footnotes';
import type { PaginatedTableBlock } from '../pagination/types';
import type { ReportData } from '../types';
import type { Alignments } from '@/types/report';
import type { TableBlock } from '@/types/report-payload';

interface StandaloneTableRendererProps {
    block: TableBlock;
    data: ReportData;
    footnotes?: ReportFootnote[];
}

export default function StandaloneTableRenderer({
    block,
    data,
    footnotes,
}: StandaloneTableRendererProps) {
    const normalizedBlock = normalizeTableBlock(block);
    const tableConfig = data.customizations?.table;
    const headerBackground = resolveTableHeaderColor(
        tableConfig?.header_color,
        data.customizations?.colors?.primary,
    );
    const headerTextColor = getContrastTextColor(headerBackground);
    const textAlignment = (tableConfig?.text_alignment || 'center') as Alignments;
    // Mesma regra do editor (`TableRenderer`), reescrita aqui porque os dois
    // caminhos de render não compartilham a célula. -1 quando a tabela não tem
    // coluna de classificação ou o laudo desligou o ajuste.
    const classificationColIndex = isClassificationColoringOn(tableConfig)
        ? getHeaderLabels(normalizedBlock.header).findIndex(
              (label) => findMetricId(label || '') === 'classification',
          )
        : -1;
    const shouldRenderDescription = !(block as PaginatedTableBlock).paginationHideDescription;

    if (!normalizedBlock.rows?.length) {
        return (
            <div className="mb-6 flex min-h-[180px] items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50 px-6 py-10 text-sm text-slate-500">
                Tabela sem dados configurados
            </div>
        );
    }

    const alignmentClass =
        textAlignment === 'left'
            ? 'text-left'
            : textAlignment === 'right'
              ? 'text-right'
              : 'text-center';
    const hasDescription = shouldRenderDescription && !!block.description?.trim();

    return (
        <>
            {hasDescription && (
                <div className="relative mb-4 border-l-2 border-slate-100 pl-5">
                    {!!footnotes?.length && (
                        <ReportFootnoteMarker
                            footnotes={footnotes}
                            className="pointer-events-none absolute left-2 top-0.5"
                        />
                    )}
                    <StandaloneRichText content={block.description} />
                </div>
            )}

            <div className="overflow-auto">
                <div className="relative inline-block min-w-full">
                    {!hasDescription && !!footnotes?.length && (
                        <ReportFootnoteMarker
                            footnotes={footnotes}
                            className="pointer-events-none absolute -top-3 left-0 z-10"
                        />
                    )}
                    <table className="w-full table-fixed border-collapse select-none text-xs">
                        <colgroup>
                            {normalizedBlock.columnWidths.map((width, index) => (
                                <col key={`col-${index}`} style={{ width }} />
                            ))}
                        </colgroup>

                        <thead>
                            {getHeaderRows(normalizedBlock).map((headerRow) => (
                                <tr key={headerRow.id}>
                                    {headerRow.cells.map((cell) => {
                                        if (cell.mergedInto) return null;

                                        return (
                                            <th
                                                key={cell.id}
                                                rowSpan={cell.rowSpan || 1}
                                                colSpan={cell.colSpan || 1}
                                                className={`border border-slate-300 px-2 py-1 align-middle text-xs font-semibold ${alignmentClass}`}
                                                style={{
                                                    backgroundColor: headerBackground,
                                                    color:
                                                        resolveTableCellColor(cell.color) ||
                                                        headerTextColor,
                                                }}
                                            >
                                                <div className="whitespace-pre-wrap break-words">
                                                    {cell.value}
                                                </div>
                                            </th>
                                        );
                                    })}
                                </tr>
                            ))}
                        </thead>

                        <tbody>
                            {normalizedBlock.rows.map((row, rowIndex) => (
                                <tr
                                    key={row.id}
                                    className={rowIndex % 2 === 0 ? 'bg-white' : 'bg-slate-50'}
                                >
                                    {row.cells.map((cell, colIndex) => {
                                        if (cell.mergedInto) return null;

                                        // A cor manual vence a derivada do rótulo.
                                        const cellColor =
                                            resolveTableCellColor(cell.color) ??
                                            (colIndex === classificationColIndex
                                                ? resolveTableCellColor(
                                                      resolveClassificationColor(cell.value),
                                                  )
                                                : undefined);

                                        return (
                                            <td
                                                key={cell.id}
                                                rowSpan={cell.rowSpan || 1}
                                                colSpan={cell.colSpan || 1}
                                                className={`border border-slate-200 px-2 py-1 align-middle text-xs ${alignmentClass}`}
                                            >
                                                <div
                                                    className="whitespace-pre-wrap break-words text-slate-700"
                                                    style={{ color: cellColor }}
                                                >
                                                    {cell.value}
                                                </div>
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}
